import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Popover,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Analytics as FunnelHealthIcon,
  AccountTree as FlowIcon,
  Campaign as CampaignIcon,
  CreditCard as PaymentsIcon,
  ErrorOutline as AnomalyIcon,
  Groups as CohortCensusIcon,
  Troubleshoot as RootCauseIcon,
  AutoAwesome as ArtifactIcon,
  HealthAndSafety as QualityIcon,
  CalendarToday as CalendarTodayIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  WebAsset as ActivePlacementsIcon,
} from '@mui/icons-material';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import { ExperimentsApiClient } from '../api/experimentsClient';
import type { ExperimentDocument } from '../experimentTypes';
import type { DecisionScorecardActions } from '../components/DecisionScorecard';
import { describeInsightsError, describeReportSectionErrors, TECH_DETAILS_LABEL, type InsightsErrorCopy } from '../api/errorCopy';
import type {
  FunnelABTestDecisionReportData,
  FunnelABTestDecisionRow,
  FunnelCampaignEconomicsRow,
  FunnelInsightsResponse,
  FunnelPaidAcquisitionFilters,
  FunnelReportRefreshResult,
  FunnelReportsCoverageData,
  FunnelReportsDashboardData,
  FunnelReportSectionError,
} from '../types';
import { SECTION_GROUP_ERROR_SECTIONS, type DashboardSectionGroup } from '../serving/sectionGroups';
import { FUNNEL_REPORTS_TEST_IDS } from '../testIds';
import { TRAFFIC_SOURCES_URL_PARAM, parseTrafficSourcesParam, type TrafficSourcesView } from '../trafficSourcesRows';
import {
  FUNNEL_UNIT_URL_PARAM,
  FunnelUnitProvider,
  applyFunnelUnitParam,
  funnelUnitQueryFragment,
  parseFunnelUnitParam,
  type FunnelUnit,
} from '../funnelUnit';
import { buildTimeZoneOptions, getBrowserTimeZone, isValidTimeZone, zonedDayBoundIso, zoneOffsetLabel } from '../timezoneWindow';
import { buildFunnelTrafficSourcesPath } from '../funnelTrafficSourcesRoute';
import {
  canonicalScopeKey,
  isCacheStale,
  readInsightsCache,
  reportsCacheKey,
  writeInsightsCache,
  type InsightsCacheEntry,
} from '../api/insightsLocalCache';
import { getAnalyticsReportBaseUrl } from '../reportEnvironment';
import { applyPaidFacetFilters } from '../serving/paidFacets';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import type { Onboarding, OnboardingVersion } from '@modules/firebase-core/converters/onboardingConverter';
import { WebPlacementService } from '@modules/firebase-core/services/WebPlacementService';
import type { WebPlacement } from '@modules/webplacements/types/webplacements.types';
import { FunnelReportsDashboard, type AbPlacementReportInfo } from '../components/FunnelReportsDashboard';
import { MetaAdsInlineConnectCard } from '@modules/meta-ads-connect';
import {
  getFunnelReportAvailability,
  hasAnyFunnelReportData,
  isReportBlockVisibleToRole,
  viewerRoleFromIsAdmin,
} from '../reportAvailability';
import type { FunnelReportsScopeMode } from '../components/FunnelReportsDashboard';
import { NotificationCenter } from '../components/NotificationCenter';
import { NeedsActionStrip } from '../components/NeedsActionStrip';
import { ReportAttentionStrip } from '../components/ReportAttentionStrip';
import { FunnelReportsBreakdownSection } from '../components/sections/FunnelReportsBreakdownSection';
import { JourneyCohortCensusSection } from '../components/sections/JourneyCohortCensusSection';
import { useInsightNotifications } from '../hooks/useInsightNotifications';
import { resolveNotificationLink } from '../notificationLinks';
import { useAuth } from '@frontend/contexts/AuthContext';
import { useProject } from '@modules/navigation/contexts';

// ---------------------------------------------------------------------------
// W-R5 (DISC-AC-133/143): the page reads its five section groups in PARALLEL
// (reports/summary + funnel + acquisition + creatives + data-health) and
// merges them into the ONE dashboard payload shape — the monolith
// reports/dashboard read is RETIRED from this page. A failed lane degrades to
// its own typed sectionErrors (PE-1); only all five failing is a page error.
// ---------------------------------------------------------------------------

/** Typed empty seed — lanes overwrite their own keys as they settle. */
function emptySplitDashboardData(): FunnelReportsDashboardData {
  return {
    summary: {
      startedJourneys: 0,
      paywallReachedJourneys: 0,
      buyButtonClicks: 0,
      offerConvertedJourneys: 0,
      trialStartedJourneys: 0,
      purchaseJourneys: 0,
      purchaseAmount: null,
      initialBillingRevenue: null,
      paywallToOfferConversionRate: null,
      paywallToOfferConversionNumerator: 0,
      paywallToOfferConversionDenominator: 0,
      buyClickToOfferConversionRate: null,
      buyClickToOfferConversionNumerator: 0,
      buyClickToOfferConversionDenominator: 0,
      minHealthScore: null,
      dataQualitySeverity: 'unknown',
    },
    aliasSummaries: [],
    aliasOnboardingSummaries: [],
    funnelHealthDaily: [],
    metricTimeseries: [],
    screenFlowEdges: [],
    paymentOutcomes: [],
    anomalies: [],
    rootCauseDrivers: [],
    artifacts: [],
    dataQuality: [],
    campaignEconomics: [],
  };
}

/** PE-1: a failed lane degrades to typed sectionErrors for ITS sections only. */
function splitLaneFailureErrors(group: DashboardSectionGroup): FunnelReportSectionError[] {
  return SECTION_GROUP_ERROR_SECTIONS[group].map((section) => ({
    section: section as FunnelReportSectionError['section'],
    code: 'query_failed',
    message: 'This report section failed to load; the other sections are unaffected.',
  }));
}

type ReportSurfaceId =
  | 'active-placements'
  | 'funnel-health'
  | 'screen-flow'
  | 'payments'
  | 'paid-acquisition'
  | 'anomaly-inbox'
  | 'root-cause'
  | 'artifacts'
  | 'data-quality'
  | 'journey-cohort-census';

interface ReportSurface {
  id: ReportSurfaceId;
  title: string;
  sourceView: string;
  pageName: string;
  purpose: string;
  Icon: typeof FunnelHealthIcon;
}

interface ReportScope {
  projectId: string;
  onboardingId: string;
  versionId: string;
  alias: string;
  from: string;
  to: string;
  target: string;
  /** IANA zone the calendar days are interpreted in ('UTC' = legacy behavior). */
  tz: string;
}

interface VersionSelectOption {
  id: string;
  name: string;
}

type PaidFilterKey = keyof FunnelPaidAcquisitionFilters;

const ALL_VERSIONS_OPTION: VersionSelectOption = {
  id: '',
  name: 'All versions',
};

const MATCH_QUALITY_OPTIONS = [
  'exact_ad_id',
  'exact_adset_id',
  'exact_campaign_id',
  'utm_object_id',
  'utm_tuple',
  'destination_url',
  'name_fallback',
  'none',
];

const PAID_FILTER_KEYS: PaidFilterKey[] = [
  'campaign',
  'metaAdAccountId',
  'metaCampaignId',
  'metaAdsetId',
  'metaAdId',
  'metaCreativeId',
  'matchQuality',
];

function paidFiltersFromSearchParams(searchParams: URLSearchParams): Record<PaidFilterKey, string> {
  return {
    campaign: searchParams.get('paidCampaign') || searchParams.get('campaign') || '',
    metaAdAccountId: searchParams.get('metaAdAccountId') || '',
    metaCampaignId: searchParams.get('metaCampaignId') || '',
    metaAdsetId: searchParams.get('metaAdsetId') || '',
    metaAdId: searchParams.get('metaAdId') || '',
    metaCreativeId: searchParams.get('metaCreativeId') || '',
    matchQuality: searchParams.get('matchQuality') || '',
  };
}

function compactPaidFilters(filters: Record<PaidFilterKey, string>): FunnelPaidAcquisitionFilters | undefined {
  const cleaned = PAID_FILTER_KEYS.reduce<FunnelPaidAcquisitionFilters>((acc, key) => {
    const value = filters[key].trim();
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

/** Dropdown option set for one paid filter: values seen in the loaded window + a context hint per value. */
interface PaidFilterOptionSet {
  values: string[];
  hints: Map<string, string>;
}

// W-R4-2 (DISC-AC-138): the instant preview is a PROJECTION of the ONE
// declared facet contract (serving/paidFacets.ts) the server post-pass runs —
// never a hand-written re-implementation. A facet added to the contract
// applies here with no second code change.

/**
 * Paid filter input: free text plus a dropdown of values that actually occurred
 * in the loaded window, so users pick campaigns/ids from evidence instead of
 * retyping Meta ids by hand.
 */
function PaidFilterAutocomplete({ testId, label, value, options, onValueChange }: {
  testId: string;
  label: string;
  value: string;
  options: PaidFilterOptionSet;
  onValueChange: (value: string) => void;
}): React.ReactElement {
  return (
    <Autocomplete
      data-testid={testId}
      freeSolo
      forcePopupIcon
      options={options.values}
      inputValue={value}
      onInputChange={(_event, nextValue) => onValueChange(nextValue)}
      fullWidth
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as { key?: React.Key } & React.HTMLAttributes<HTMLLIElement>;
        const hint = options.hints.get(option);
        return (
          <li key={key ?? option} {...optionProps}>
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{option}</Typography>
              {hint ? (
                <Typography variant="caption" color="text.secondary" noWrap>{hint}</Typography>
              ) : null}
            </Stack>
          </li>
        );
      }}
      renderInput={params => <TextField {...params} label={label} size="small" />}
    />
  );
}

/** FU part D (D-F1): payload snapshot for the ONE-entry-per-project cache. */
interface ReportsCachePayloads {
  scope: ReportScope;
  paidFilters: Record<PaidFilterKey, string>;
  dashboard: FunnelInsightsResponse<FunnelReportsDashboardData> | null;
  decisions: FunnelInsightsResponse<FunnelABTestDecisionReportData> | null;
  coverage: FunnelReportsCoverageData | null;
}

function reportsScopeKey(scope: ReportScope, paidFilters: Record<PaidFilterKey, string>): string {
  // tz='UTC' canonicalizes to absent — the legacy scope identity — so caches
  // written before the timezone feature stay valid.
  return canonicalScopeKey({
    ...scope,
    tz: scope.tz === 'UTC' ? '' : scope.tz,
    ...(compactPaidFilters(paidFilters) ?? {}),
  });
}

const EXPLICIT_SCOPE_PARAMS = [
  'onboardingId', 'versionId', 'alias', 'from', 'to', 'target', 'tz',
  'paidCampaign', 'campaign', 'metaAdAccountId', 'metaCampaignId',
  'metaAdsetId', 'metaAdId', 'metaCreativeId', 'matchQuality',
];

/**
 * D-F1 mount rule: a URL without explicit scope restores the cached applied
 * scope wholesale; explicit URL params serve from cache only on an EXACT
 * scope-identity match (a partial URL scope can never match a cached window).
 */
function loadInitialReportsCache(
  projectId: string,
  searchParams: URLSearchParams,
): InsightsCacheEntry<ReportsCachePayloads> | null {
  if (!projectId) return null;
  const entry = readInsightsCache<ReportsCachePayloads>(reportsCacheKey(projectId));
  if (!entry?.payloads?.scope || !entry.payloads.dashboard) return null;
  const hasExplicitScope = EXPLICIT_SCOPE_PARAMS.some(param => searchParams.get(param));
  if (!hasExplicitScope) return entry;
  const urlScope: ReportScope = {
    projectId,
    onboardingId: searchParams.get('onboardingId') || '',
    versionId: searchParams.get('versionId') || '',
    alias: searchParams.get('alias') || '',
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
    target: searchParams.get('target') || 'reports',
    tz: searchParams.get('tz') || 'UTC',
  };
  return reportsScopeKey(urlScope, paidFiltersFromSearchParams(searchParams)) === entry.scopeKey ? entry : null;
}

export const REPORT_SURFACES: ReportSurface[] = [
  {
    id: 'active-placements',
    title: 'Active Placements',
    pageName: 'Active Web Placements',
    sourceView: 'events_deduped + customer_subscription_fact',
    purpose: 'Aliases with traffic, active onboarding versions, journey gates, revenue, and data quality for the selected date range.',
    Icon: ActivePlacementsIcon,
  },
  {
    id: 'funnel-health',
    title: 'Funnel Health',
    pageName: 'Executive Funnel Health',
    sourceView: 'viz_funnel_health_daily',
    purpose: 'Starts, paywall reach, buy intent, conversions, revenue, ARPU, and data quality severity.',
    Icon: FunnelHealthIcon,
  },
  {
    id: 'screen-flow',
    title: 'Screen Flow',
    pageName: 'Screen Flow And Dropoff',
    sourceView: 'viz_screen_flow_edges',
    purpose: 'Forward, back, jump, and drop-off transitions between V2 screens.',
    Icon: FlowIcon,
  },
  {
    id: 'payments',
    title: 'Payments And Revenue',
    pageName: 'Payments And Revenue',
    sourceView: 'customer_subscription_fact',
    purpose: 'Purchases, trials, paid trials, subscriptions, initial amount, and LTV facts.',
    Icon: PaymentsIcon,
  },
  {
    id: 'paid-acquisition',
    title: 'Paid Acquisition',
    pageName: 'Meta Ads And Campaign Economics',
    sourceView: 'sessions + meta_ads_delivery_fact',
    purpose: 'Optional Meta Ads enrichment for spend, matched paid sessions, conversion costs, match quality, and unmatched ad spend.',
    Icon: CampaignIcon,
  },
  // W-R6 (DISC-AC-150): the `creative-diagnostics` surface entry retired with
  // the standalone section — diagnostics now lives as a CD saved view
  // (`/reports/creative-decisions?view=diagnostics`).
  {
    id: 'anomaly-inbox',
    title: 'Anomaly Inbox',
    pageName: 'Anomaly Inbox',
    sourceView: 'viz_anomaly_inbox',
    purpose: 'Metric deltas, severity, revenue impact, and data-quality suppression status.',
    Icon: AnomalyIcon,
  },
  {
    id: 'root-cause',
    title: 'Root Cause And Variables',
    pageName: 'Root Cause And Variables',
    sourceView: 'viz_root_cause_drivers',
    purpose: 'Top screen, edge, product, traffic, country, and allowlisted variable contributors.',
    Icon: RootCauseIcon,
  },
  {
    id: 'artifacts',
    title: 'Artifacts And Creatives',
    pageName: 'Artifacts And Creatives',
    sourceView: 'viz_artifact_performance',
    purpose: 'Deterministic artifact keys, hashes, kinds, and creative-surface performance.',
    Icon: ArtifactIcon,
  },
  {
    id: 'data-quality',
    title: 'Data Quality',
    pageName: 'Data Quality',
    sourceView: 'viz_data_quality_health',
    purpose: 'Freshness, nulls, duplicates, missing coverage, and report trust guardrails.',
    Icon: QualityIcon,
  },
  {
    id: 'journey-cohort-census',
    title: 'Journey Cohort Census',
    pageName: 'Journey Cohort Census',
    sourceView: 'calibration artifacts + cohorts + segment scan',
    purpose: 'Who actually travels this funnel: model cohorts read from audience-calibration artifacts, saved cohorts, and source × device × geo cells. Observational shares — not causal effects.',
    Icon: CohortCensusIcon,
  },
];

const REPORT_REFRESH_TARGET_OPTIONS = [
  'reports',
  'all',
  'funnel',
  'billing',
  'events_deduped',
  'variable_updates',
  'sessions',
  'user_profile_latest',
  'user_trait_history',
  'journey_facts',
  'screen_nodes',
  'screen_edges',
  'data_quality_issue',
  'data_quality_health',
  'metric_timeseries',
  'anomaly_candidate',
  'anomaly_contribution',
  'subscription_state_current',
  'subscription_state_history',
  'customer_subscription_fact',
];
const PROJECT_SYNC_INTERVAL_MS = 15 * 60 * 1000;
const DAY_MS = 86_400_000;

/** PE-6 (D-P4): the draft-vs-applied fields the Apply button reconciles. */
const SCOPE_COMPARE_KEYS: Array<keyof ReportScope> = [
  'onboardingId',
  'versionId',
  'alias',
  'from',
  'to',
  'target',
];

function defaultIsoRange(): Pick<ReportScope, 'from' | 'to'> {
  const now = new Date();
  const end = new Date(now.getTime() + 5 * 60 * 1000);
  const start = new Date(now);
  start.setUTCHours(0, 0, 0, 0);
  return {
    from: start.toISOString(),
    to: end.toISOString(),
  };
}

export function buildReportSurfaceUrl(
  baseUrl: string,
  surface: ReportSurface,
  scope: ReportScope,
): string | null {
  if (!baseUrl) return null;
  const url = new URL(baseUrl);
  url.searchParams.set('reportPage', surface.id);
  url.searchParams.set('projectId', scope.projectId);
  if (scope.onboardingId) url.searchParams.set('onboardingId', scope.onboardingId);
  if (scope.versionId) url.searchParams.set('versionId', scope.versionId);
  if (scope.alias) url.searchParams.set('alias', scope.alias);
  url.searchParams.set('from', scope.from);
  url.searchParams.set('to', scope.to);
  return url.toString();
}

function formatUtc(value: string): string {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
}

function dateTimeInputFromIso(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
}

function dateTimeInputToIso(value: string): string {
  if (!value) return '';
  const date = new Date(`${value}:00.000Z`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
}

function formatUtcDateTime(value: string): string {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** PE-6 (D-P2): human window bounds — coverage days are UTC DATE strings. */
function formatCoverageDate(isoDay: string): string {
  const date = new Date(`${isoDay}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return isoDay;
  return `${MONTH_SHORT[date.getUTCMonth()]} ${String(date.getUTCDate()).padStart(2, '0')}`;
}

/**
 * A3: the header trigger states the window the way the mock does —
 * `Jul 31 – Aug 6, 2026`. `to` is END-EXCLUSIVE everywhere in this module, so
 * the label shows the last INCLUDED day; otherwise a 7-day window would read as
 * eight days and the reader would mistrust every number under it.
 */
function formatWindowRangeLabel(fromIso: string, toIso: string): string {
  const from = new Date(fromIso);
  const lastIncluded = new Date(Date.parse(toIso) - 1);
  if (Number.isNaN(from.getTime()) || Number.isNaN(lastIncluded.getTime())) return 'Select a range';
  const day = (date: Date): string => `${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}`;
  const year = lastIncluded.getUTCFullYear();
  if (day(from) === day(lastIncluded) && from.getUTCFullYear() === year) return `${day(from)}, ${year}`;
  return `${day(from)} – ${day(lastIncluded)}, ${year}`;
}

/** Whole days covered by an end-exclusive window; 0 when either bound is unset. */
function windowLengthDays(fromIso: string, toIso: string): number {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return 0;
  return Math.max(1, Math.round((to - from) / 86_400_000));
}

interface ReadyPeriodOption {
  value: string;
  label: string;
  from: string;
  to: string;
}

/**
 * PE-6 (D-P2): rolling windows of the active preset length walked back from
 * the last covered day (end exclusive), while they still touch coverage —
 * max 8 entries. Selecting one only stages a draft; Apply owns the fetch.
 */
function buildReadyPeriodOptions(
  coverage: FunnelReportsCoverageData | null,
  lengthDays: number,
): ReadyPeriodOption[] {
  if (!coverage?.maxDate || !coverage.minDate) return [];
  const anchorMs = Date.parse(`${coverage.maxDate}T00:00:00.000Z`) + DAY_MS;
  const minMs = Date.parse(`${coverage.minDate}T00:00:00.000Z`);
  if (!Number.isFinite(anchorMs) || !Number.isFinite(minMs)) return [];
  const options: ReadyPeriodOption[] = [];
  for (let index = 0; index < 8; index += 1) {
    const toMs = anchorMs - index * lengthDays * DAY_MS;
    if (toMs <= minMs) break;
    const fromMs = toMs - lengthDays * DAY_MS;
    const fromDay = new Date(fromMs).toISOString().slice(0, 10);
    const lastDay = new Date(toMs - DAY_MS).toISOString().slice(0, 10);
    const from = new Date(fromMs).toISOString();
    const to = new Date(toMs).toISOString();
    options.push({
      value: `${from}|${to}`,
      from,
      to,
      label: `${formatCoverageDate(fromDay)} – ${formatCoverageDate(lastDay)}${index === 0 ? ' (latest)' : ''}`,
    });
  }
  return options;
}

function getNextProjectSyncAt(nowMs: number): Date {
  return new Date((Math.floor(nowMs / PROJECT_SYNC_INTERVAL_MS) + 1) * PROJECT_SYNC_INTERVAL_MS);
}

function formatTimeUntil(targetMs: number, nowMs: number): string {
  const remainingMs = Math.max(0, targetMs - nowMs);
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

function formatProjectSyncStatus(latestMaterializedAt: string | undefined, nowMs: number): string {
  const nextSyncAt = getNextProjectSyncAt(nowMs);
  const nextSyncLabel = `Next project sync in ${formatTimeUntil(nextSyncAt.getTime(), nowMs)} (${formatUtcDateTime(nextSyncAt.toISOString())})`;
  if (!latestMaterializedAt) return `${nextSyncLabel}.`;
  return `${nextSyncLabel}. Last materialized: ${formatUtcDateTime(latestMaterializedAt)}.`;
}

function hasRequiredRefreshScope(scope: ReportScope): boolean {
  return Boolean(scope.projectId && scope.onboardingId && scope.versionId && scope.from && scope.to);
}

function hasRequiredDashboardScope(scope: ReportScope): boolean {
  return Boolean(scope.projectId && scope.from && scope.to);
}

/**
 * PE-6b (D-P5): compare=prior is requested ONLY when coverage confirms at
 * least one covered day inside the adjacent-left window of the same length.
 */
function hasPriorCoverageFor(
  coverage: FunnelReportsCoverageData | null,
  fromIso: string,
  toIso: string,
): boolean {
  if (!coverage?.days?.length) return false;
  const fromMs = Date.parse(fromIso);
  const toMs = Date.parse(toIso);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return false;
  const priorFromMs = fromMs - (toMs - fromMs);
  return coverage.days.some(day => {
    const dayStartMs = Date.parse(`${day.reportDate}T00:00:00.000Z`);
    return Number.isFinite(dayStartMs) && dayStartMs + DAY_MS > priorFromMs && dayStartMs < fromMs;
  });
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatOnboardingLabel(onboarding: Pick<Onboarding, 'id' | 'name'>): string {
  return `${onboarding.name || 'Untitled onboarding'} (${onboarding.id})`;
}

function formatVersionLabel(version: Pick<OnboardingVersion, 'id' | 'name'>): string {
  if (!version.id) return 'All versions';
  return `${version.name || 'Untitled version'} (${version.id})`;
}

function formatAliasLabel(placement: Pick<WebPlacement, 'id' | 'alias' | 'name'>): string {
  return `${placement.alias} - ${placement.name || 'Untitled placement'}`;
}

function scrollToTestId(testId: string): void {
  const target = document.querySelector(`[data-testid="${testId}"]`);
  target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

function getScopeMode(scope: ReportScope): FunnelReportsScopeMode {
  if (scope.versionId) return 'version';
  if (scope.onboardingId) return 'onboarding';
  if (scope.alias) return 'alias';
  return 'project';
}

function surfaceAppliesToScope(surfaceId: ReportSurfaceId, scopeMode: FunnelReportsScopeMode): boolean {
  if (scopeMode === 'project' || scopeMode === 'alias') {
    return surfaceId !== 'screen-flow' && surfaceId !== 'artifacts';
  }
  return true;
}

export interface FunnelReportsPageProps {
  creativeDecisionsEnabled?: boolean;
}

export function FunnelReportsPage({
  creativeDecisionsEnabled = false,
}: FunnelReportsPageProps = {}): React.ReactElement {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const range = React.useMemo(() => defaultIsoRange(), []);
  // FU part D (D-F1): a repeat visit renders the LAST SAVED payloads with ZERO
  // network; fresh data loads ONLY on explicit action (Refresh now / Apply /
  // QA refresh / drill-down). The entry is read once per mount.
  const [initialCache] = React.useState(() => loadInitialReportsCache(projectId, searchParams));
  const [servedFromCache, setServedFromCache] = React.useState(Boolean(initialCache));
  const [scope, setScope] = React.useState<ReportScope>(() => (initialCache
    ? { ...initialCache.payloads.scope, projectId }
    : {
      projectId,
      onboardingId: searchParams.get('onboardingId') || '',
      versionId: searchParams.get('versionId') || '',
      alias: searchParams.get('alias') || '',
      from: searchParams.get('from') || range.from,
      to: searchParams.get('to') || range.to,
      target: searchParams.get('target') || 'reports',
      tz: isValidTimeZone(searchParams.get('tz') || '') ? searchParams.get('tz') as string : 'UTC',
    }));
  const [paidFilters, setPaidFilters] = React.useState<Record<PaidFilterKey, string>>(
    () => (initialCache ? initialCache.payloads.paidFilters : paidFiltersFromSearchParams(searchParams)),
  );
  // PE-6 (D-P4): `scope`/`paidFilters` stay the APPLIED state (the only fetch
  // input); the form edits these drafts and the Apply button reconciles them.
  const [draftScope, setDraftScope] = React.useState<ReportScope>(scope);
  // F2 (FT-AC-084, D4): the page counting unit — INSTANT, never part of the
  // draft/Apply cycle (both grains already ride one payload; a unit change
  // must not silently carry other pending scope edits). URL param is the
  // ONLY carrier (D2) — replaceState keeps the router quiet. Parse/apply are
  // the symmetric pair from funnelUnit.tsx: never inline the inversion here,
  // or this surface can drift from the canvas one (F4).
  const [funnelUnit, setFunnelUnit] = React.useState<FunnelUnit>(
    () => parseFunnelUnitParam(searchParams.get(FUNNEL_UNIT_URL_PARAM)),
  );
  const handleFunnelUnitChange = React.useCallback((unit: FunnelUnit) => {
    setFunnelUnit(unit);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    applyFunnelUnitParam(url.searchParams, unit);
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);
  // A5: the hidden toggle no longer calls this, but the MODE is not removed —
  // the `unit` param still selects it on load, and B3's display-only «Show
  // visits» on the gates band is now its caller.
  const handleShowVisits = React.useCallback(() => handleFunnelUnitChange('visits'), [handleFunnelUnitChange]);
  // People-attribution S6: the Traffic sources view rides the URL like the
  // unit (`traffic=campaigns|creatives`), replaceState only — never part of
  // the draft/Apply cycle, never a refetch.
  const [trafficView, setTrafficView] = React.useState<TrafficSourcesView | null>(
    () => parseTrafficSourcesParam(searchParams.get(TRAFFIC_SOURCES_URL_PARAM)),
  );
  const handleTrafficViewChange = React.useCallback((view: TrafficSourcesView | null) => {
    setTrafficView(view);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (view) url.searchParams.set(TRAFFIC_SOURCES_URL_PARAM, view);
    else url.searchParams.delete(TRAFFIC_SOURCES_URL_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);
  // reports-timezone-select: options are static per session; browser zone is
  // surfaced right after UTC in the list.
  const timeZoneOptions = React.useMemo(() => buildTimeZoneOptions(), []);
  const browserTimeZone = React.useMemo(() => getBrowserTimeZone(), []);
  const [draftPaidFilters, setDraftPaidFilters] = React.useState<Record<PaidFilterKey, string>>(paidFilters);
  // PE-6a (D-P3/D-P5): coverage is the honest ready-period source. The FIRST
  // dashboard read always waits for it to settle — it resolves the default
  // window (no-range URLs) AND the compare=prior decision; failure settles
  // too, degrading to envelope anchoring without prior comparison.
  const [coverage, setCoverage] = React.useState<FunnelReportsCoverageData | null>(initialCache?.payloads.coverage ?? null);
  const coverageRef = React.useRef<FunnelReportsCoverageData | null>(initialCache?.payloads.coverage ?? null);
  // A cache-served mount already knows its window — Refresh must not re-wait.
  // W-R4-2 (DISC-AC-142b): a URL that names an explicit from/to starts the
  // dashboard IMMEDIATELY — coverage resolves in parallel and only corrects
  // the range picker; when it later confirms a covered prior window, the
  // prior comparison upgrades via the cheap reports/summary refetch (the
  // DISC-AC-133 split endpoint), never a monolith re-run.
  const [rangeReady, setRangeReady] = React.useState(Boolean(initialCache));
  const [dryRun, setDryRun] = React.useState(searchParams.get('dryRun') === 'true');
  const [showEmptyReports, setShowEmptyReports] = React.useState(searchParams.get('showEmptyReports') === 'true');
  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshResult, setRefreshResult] = React.useState<FunnelInsightsResponse<FunnelReportRefreshResult> | null>(null);
  const [refreshError, setRefreshError] = React.useState<string | null>(null);
  const [dashboardResult, setDashboardResult] = React.useState<FunnelInsightsResponse<FunnelReportsDashboardData> | null>(initialCache?.payloads.dashboard ?? null);
  const [abDecisionResult, setABDecisionResult] = React.useState<FunnelInsightsResponse<FunnelABTestDecisionReportData> | null>(initialCache?.payloads.decisions ?? null);
  // R3-3: registry experiments backing the decision buttons (linked by abTestRef.abTestId).
  const [registryExperiments, setRegistryExperiments] = React.useState<ExperimentDocument[]>([]);
  const [loadingDashboard, setLoadingDashboard] = React.useState(false);
  const [dashboardError, setDashboardError] = React.useState<InsightsErrorCopy | null>(null);
  const [abDecisionError, setABDecisionError] = React.useState<string | null>(null);
  const [dashboardReloadKey, setDashboardReloadKey] = React.useState(0);
  // F3 (FT-AC-089): explicit attribution model for the paid lane. undefined =
  // the served default; page-session state ONLY (no URL, no localStorage) so
  // a fresh page load always selects the served default. The ref mirrors the
  // state for the lane effect (coverageRef precedent — a switch must not
  // refire every lane; scope Apply carries the current selection through it).
  const [attributionModelId, setAttributionModelId] = React.useState<string | undefined>(undefined);
  // S10: the version row whose «Sources» popup is open (null = closed).
  // S11 §14.1 (owner): the version row's «Sources» opens the D-M page in a NEW
  // TAB by default — one behaviour, the Reports page stays behind (the popup
  // component is kept but no longer mounted here).
  const openVersionSources = React.useCallback((ref: { alias: string; onboardingId: string; versionId: string; versionName?: string; onboardingName?: string }) => {
    const path = buildFunnelTrafficSourcesPath({
      projectId: scope.projectId,
      onboardingId: ref.onboardingId,
      versionId: ref.versionId,
      from: scope.from,
      to: scope.to,
      tz: scope.tz,
      alias: ref.alias || undefined,
      unit: funnelUnit,
      versionName: ref.versionName,
      onboardingName: ref.onboardingName,
    });
    window.open(path, '_blank', 'noopener');
  }, [scope.projectId, scope.from, scope.to, scope.tz, funnelUnit]);
  const attributionModelRef = React.useRef<string | undefined>(undefined);
  attributionModelRef.current = attributionModelId;
  const [attributionSwitchPending, setAttributionSwitchPending] = React.useState(false);
  const [clockNowMs, setClockNowMs] = React.useState(() => Date.now());
  const [onboardings, setOnboardings] = React.useState<Onboarding[]>([]);
  const [versions, setVersions] = React.useState<OnboardingVersion[]>([]);
  const [webPlacements, setWebPlacements] = React.useState<WebPlacement[]>([]);
  // FU part E (D-F2): the UNFILTERED placement list — A/B detection + variant
  // resolution need ab_test docs and aliasless children the select drops.
  const [allPlacements, setAllPlacements] = React.useState<WebPlacement[]>([]);
  const [loadingOnboardings, setLoadingOnboardings] = React.useState(false);
  const [loadingVersions, setLoadingVersions] = React.useState(false);
  const [loadingAliases, setLoadingAliases] = React.useState(false);
  const [scopeSelectorError, setScopeSelectorError] = React.useState<string | null>(null);
  const client = React.useMemo(() => new FunnelInsightsApiClient(), []);
  const experimentsClient = React.useMemo(() => new ExperimentsApiClient(), []);

  // R3-3: load registry experiments once decision rows exist, so scorecards can
  // offer Declare-winner/Stop-test against the registry transition endpoint.
  const hasDecisionRows = Boolean(abDecisionResult?.data.decisions.length);
  React.useEffect(() => {
    if (!projectId || !hasDecisionRows) return;
    let cancelled = false;
    experimentsClient.list(projectId)
      .then(list => { if (!cancelled) setRegistryExperiments(list); })
      .catch(error => {
        // Degrade honestly: without the registry list the cards stay read-only.
        console.warn('Reports: experiments registry unavailable — decision buttons hidden', error);
      });
    return () => { cancelled = true; };
  }, [projectId, hasDecisionRows, experimentsClient]);

  const decisionActionsFor = React.useCallback((row: FunnelABTestDecisionRow): DecisionScorecardActions | undefined => {
    if (!projectId) return undefined;
    const experiment = registryExperiments.find(doc => doc.abTestRef?.abTestId === row.testId);
    if (!experiment) return undefined;
    return {
      experimentState: experiment.state,
      onTransition: async (action, payload) => {
        const updated = await experimentsClient.transition(projectId, experiment.id, { action, ...payload });
        setRegistryExperiments(current => current.map(doc => (doc.id === updated.id ? updated : doc)));
      },
    };
  }, [projectId, registryExperiments, experimentsClient]);
  const onboardingService = React.useMemo(() => new OnboardingService(), []);
  const webPlacementService = React.useMemo(() => new WebPlacementService(), []);
  const reportBaseUrl = getAnalyticsReportBaseUrl();
  const { isAdmin } = useAuth();
  const { userRole, projectName } = useProject();
  // R1-2 (B8): presets anchor to the freshest materialized data, never to "now".
  const [activeDatePreset, setActiveDatePreset] = React.useState<7 | 28 | null>(null);
  const [qaMenuAnchor, setQaMenuAnchor] = React.useState<HTMLElement | null>(null);
  // A3: the header's date trigger owns the picker popover.
  const [dateMenuAnchor, setDateMenuAnchor] = React.useState<HTMLElement | null>(null);
  const urlHadExplicitRange = React.useRef(Boolean(searchParams.get('from') || searchParams.get('to')));
  // A cache-restored window must never be re-snapped by a later coverage answer.
  const didDefaultSnapRange = React.useRef(Boolean(initialCache));
  // QA controls are gated per item (handoff B3): manual refresh + sync status
  // stay owner-visible — the owner's only on-demand re-materialization path
  // (P12); Dry run / Target / Show empty sections are platform-admin only.
  // The showEmptyReports URL param keeps working regardless of the toggle.
  const canUseRefresh = userRole === 'owner' || Boolean(isAdmin);
  const showQaControls = Boolean(isAdmin);
  // E (VISIBILITY_SPEC decision 2): section visibility follows `isAdmin` ALONE.
  // A project owner keeps refresh (above) and gets the same reduced page as any
  // other member — the two gates must not be collapsed into one.
  const viewerRole = viewerRoleFromIsAdmin(isAdmin);
  const canSeeBlock = React.useCallback(
    (id: Parameters<typeof isReportBlockVisibleToRole>[0]) => isReportBlockVisibleToRole(id, viewerRole),
    [viewerRole],
  );
  const activePaidFilterCount = Object.keys(compactPaidFilters(draftPaidFilters) ?? {}).length;
  // A4: one accordion now holds the scope pickers AND the paid filters, so its
  // summary counts both — otherwise a reader with an onboarding filter applied
  // would see «0» on the block that is hiding it.
  const activeScopeFilterCount = [draftScope.onboardingId, draftScope.versionId, draftScope.alias]
    .filter(value => Boolean(value)).length;
  const activeAdvancedFilterCount = activePaidFilterCount + activeScopeFilterCount;

  React.useEffect(() => {
    const timer = window.setInterval(() => setClockNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  React.useEffect(() => {
    setScope(current => ({ ...current, projectId }));
    setDraftScope(current => ({ ...current, projectId }));
  }, [projectId]);

  // PE-6a (D-P1/D-P3): fetch coverage once per APPLIED scope identity (never
  // per draft keystroke — BQ quota). The first response resolves the default
  // window (last 7 covered days ending maxDate) when the URL had no range;
  // any failure degrades to envelope anchoring and never blocks the dashboard.
  React.useEffect(() => {
    if (!scope.projectId) return undefined;
    // D-F1: zero network while serving the cached snapshot.
    if (servedFromCache) return undefined;
    const controller = new AbortController();
    client.getReportsCoverage({
      projectId: scope.projectId,
      onboardingId: scope.onboardingId || undefined,
      versionId: scope.versionId || undefined,
      alias: scope.alias || undefined,
      signal: controller.signal,
    })
      .then(result => {
        if (controller.signal.aborted) return;
        coverageRef.current = result.data;
        setCoverage(result.data);
        if (!urlHadExplicitRange.current && !didDefaultSnapRange.current && result.data.maxDate) {
          const toIso = new Date(Date.parse(`${result.data.maxDate}T00:00:00.000Z`) + DAY_MS).toISOString();
          const fromIso = new Date(Date.parse(toIso) - 7 * DAY_MS).toISOString();
          setActiveDatePreset(7);
          setDraftScope(current => ({ ...current, from: fromIso, to: toIso }));
          setScope(current => ({ ...current, from: fromIso, to: toIso }));
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        coverageRef.current = null;
        setCoverage(null);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        // The default-open snap has exactly one shot: the first coverage answer.
        didDefaultSnapRange.current = true;
        setRangeReady(true);
      });
    return () => controller.abort();
  }, [client, servedFromCache, scope.projectId, scope.onboardingId, scope.versionId, scope.alias]);

  // W-R4-2 (DISC-AC-142b): URL-explicit ranges never wait for coverage. The
  // derived flag is TRUE from the first render for explicit URLs, so the late
  // rangeReady flip does not re-fire the fetch (no double monolith run).
  const dashboardCanStart = rangeReady || urlHadExplicitRange.current;

  React.useEffect(() => {
    // PE-6 (D-P3): a default-window open (no URL range) still waits for
    // coverage so the page opens directly on the last READY period — one
    // fetch, not two; explicit-range URLs start immediately (DISC-AC-142b).
    if (!dashboardCanStart) return undefined;
    // D-F1: zero network while serving the cached snapshot.
    if (servedFromCache) return undefined;
    if (!hasRequiredDashboardScope(scope)) {
      setDashboardResult(null);
      setABDecisionResult(null);
      setDashboardError(null);
      setABDecisionError(null);
      return undefined;
    }

    const controller = new AbortController();
    setLoadingDashboard(true);
    setDashboardError(null);
    setABDecisionResult(null);
    setABDecisionError(null);
    // reports-timezone-select: calendar day bounds shift to the chosen
    // zone's midnights — the server filters core numbers by exact instants.
    const reportRequest = {
      projectId: scope.projectId,
      onboardingId: scope.onboardingId || undefined,
      versionId: scope.versionId || undefined,
      alias: scope.alias,
      from: zonedDayBoundIso(scope.from, scope.tz),
      to: zonedDayBoundIso(scope.to, scope.tz),
      tz: scope.tz && scope.tz !== 'UTC' ? scope.tz : undefined,
      target: scope.target,
      paidFilters: compactPaidFilters(paidFilters),
      limit: 25,
      signal: controller.signal,
    };
    // PE-6b (D-P5): ask for the prior-period summary ONLY when coverage
    // confirms at least one covered day in the adjacent-left window — a read
    // through the ref so a late coverage answer never refires this effect.
    const wantsPriorCompare = hasPriorCoverageFor(coverageRef.current, scope.from, scope.to);

    // W-R5 (DISC-AC-133/143): five split lanes replace the ONE monolith read.
    // Lanes merge into the single dashboardResult as they settle (the header
    // paints first, sections stream — Story B-14); the summary lane is the
    // freshness/asOf authority (its inputs run the data-boundary queries).
    const splitLanes: Array<{
      group: DashboardSectionGroup;
      fetch: () => Promise<FunnelInsightsResponse<Partial<FunnelReportsDashboardData>>>;
    }> = [
      {
        group: 'summary',
        fetch: () => client.getReportsSummary({
          ...reportRequest,
          ...(wantsPriorCompare && { compare: 'prior' as const }),
        }),
      },
      { group: 'funnel', fetch: () => client.getReportsFunnelSection(reportRequest) },
      // F3 (FT-AC-089): the acquisition lane carries the page's current model
      // selection (ref read — a switch refetches ONLY this lane, below).
      {
        group: 'acquisition',
        fetch: () => client.getReportsAcquisition({
          ...reportRequest,
          attributionModelId: attributionModelRef.current,
        }),
      },
      // W-R6 (DISC-AC-149/150): the creatives lane retired with its sections —
      // the CD saved views fetch `reports/creatives?view=` themselves.
      { group: 'data-health', fetch: () => client.getReportsDataHealth(reportRequest) },
    ];
    // Lane outcomes accumulate in this append-only ledger; every state update
    // derives from it PURELY (React updaters may re-run — no closure splices).
    const settledLanes: Array<{
      group: DashboardSectionGroup;
      result?: FunnelInsightsResponse<Partial<FunnelReportsDashboardData>>;
    }> = [];
    const applySettledLanes = () => {
      setDashboardResult(() => {
        const succeeded = settledLanes.filter(lane => lane.result);
        if (!succeeded.length) return null;
        const summaryLane = succeeded.find(lane => lane.group === 'summary');
        const baseLane = summaryLane ?? succeeded[0];
        const merged: FunnelInsightsResponse<FunnelReportsDashboardData> = {
          ...(baseLane.result as FunnelInsightsResponse<Partial<FunnelReportsDashboardData>>),
          data: emptySplitDashboardData(),
        } as FunnelInsightsResponse<FunnelReportsDashboardData>;
        const sectionErrors: FunnelReportSectionError[] = [];
        const warnings: FunnelInsightsResponse<unknown>['warnings'] = [];
        const seenWarnings = new Set<string>();
        for (const lane of settledLanes) {
          if (!lane.result) {
            sectionErrors.push(...splitLaneFailureErrors(lane.group));
            continue;
          }
          const incoming = { ...lane.result.data } as Partial<FunnelReportsDashboardData>;
          if (incoming.sectionErrors) {
            sectionErrors.push(...incoming.sectionErrors);
            delete incoming.sectionErrors;
          }
          merged.data = { ...merged.data, ...incoming };
          for (const warning of lane.result.warnings ?? []) {
            const key = `${warning.code}|${warning.message}`;
            if (seenWarnings.has(key)) continue;
            seenWarnings.add(key);
            warnings.push(warning);
          }
        }
        if (sectionErrors.length) merged.data.sectionErrors = sectionErrors;
        merged.warnings = warnings;
        return merged;
      });
    };
    let laneFailures = 0;
    let lastLaneError: unknown = null;
    void Promise.allSettled(splitLanes.map(async lane => {
      try {
        const result = await lane.fetch();
        if (controller.signal.aborted) return;
        settledLanes.push({ group: lane.group, result });
        applySettledLanes();
      } catch (error) {
        if (controller.signal.aborted) return;
        laneFailures += 1;
        lastLaneError = error;
        settledLanes.push({ group: lane.group });
        applySettledLanes();
      }
    })).then(() => {
      if (controller.signal.aborted) return;
      if (laneFailures === splitLanes.length) {
        setDashboardResult(null);
        setDashboardError(lastLaneError instanceof Error
          ? describeInsightsError(lastLaneError)
          : { headline: 'Failed to load reports dashboard', severity: 'error' });
      }
      setLoadingDashboard(false);
    });

    client.getABTestDecisionReport(reportRequest)
      .then(result => {
        if (!controller.signal.aborted) {
          setABDecisionResult(result);
        }
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        setABDecisionResult(null);
        setABDecisionError(isOptionalABDecisionReportMissing(error)
          ? null
          : error instanceof Error ? error.message : 'Failed to load A/B decision report');
      });

    return () => {
      controller.abort();
    };
  }, [
    client,
    dashboardCanStart,
    servedFromCache,
    scope.projectId,
    scope.onboardingId,
    scope.versionId,
    scope.alias,
    scope.from,
    scope.to,
    scope.target,
    scope.tz,
    paidFilters.campaign,
    paidFilters.metaAdAccountId,
    paidFilters.metaCampaignId,
    paidFilters.metaAdsetId,
    paidFilters.metaAdId,
    paidFilters.metaCreativeId,
    paidFilters.matchQuality,
    dashboardReloadKey,
  ]);

  // F3 (FT-AC-089, D-F3-3): a model switch refetches ONLY the acquisition
  // lane under the APPLIED scope and merges its payload keys into the merged
  // dashboard result (the lane-settlement idiom). Failure reverts the
  // selection and keeps the previous payload — the caption never claims a
  // model the numbers were not computed under.
  const handleAttributionModelChange = React.useCallback((modelId: string) => {
    if (!hasRequiredDashboardScope(scope)) return;
    const previous = attributionModelRef.current;
    setAttributionModelId(modelId);
    setAttributionSwitchPending(true);
    client.getReportsAcquisition({
      projectId: scope.projectId,
      onboardingId: scope.onboardingId || undefined,
      versionId: scope.versionId || undefined,
      alias: scope.alias,
      from: zonedDayBoundIso(scope.from, scope.tz),
      to: zonedDayBoundIso(scope.to, scope.tz),
      tz: scope.tz && scope.tz !== 'UTC' ? scope.tz : undefined,
      target: scope.target,
      paidFilters: compactPaidFilters(paidFilters),
      limit: 25,
      attributionModelId: modelId,
    })
      .then(result => {
        setDashboardResult(current => {
          if (!current) return current;
          const incoming = { ...result.data } as Partial<FunnelReportsDashboardData>;
          const incomingErrors = incoming.sectionErrors ?? [];
          delete incoming.sectionErrors;
          const ownSections = new Set<string>(SECTION_GROUP_ERROR_SECTIONS.acquisition);
          const keptErrors = (current.data.sectionErrors ?? []).filter(entry => !ownSections.has(entry.section));
          const sectionErrors = [...keptErrors, ...incomingErrors];
          const data = { ...current.data, ...incoming };
          if (sectionErrors.length) data.sectionErrors = sectionErrors;
          else delete data.sectionErrors;
          return { ...current, data };
        });
      })
      .catch(error => {
        console.warn('Reports: attribution model switch failed — selection reverted', error);
        setAttributionModelId(previous);
      })
      .finally(() => setAttributionSwitchPending(false));
  }, [client, scope, paidFilters]);

  // W-R4-2 (DISC-AC-134): lazy per-campaign creative drilldown loader — the
  // dashboard's campaign expand fetches its creatives on demand under the
  // APPLIED scope + filters (a filter change re-keys the request, so stale
  // cursors are refused server-side per CURSOR-KEEP).
  // S6: the creatives tab of the Traffic sources view reads the standalone
  // performance lane for the whole scope (DISC-AC-149 — its own lane).
  const loadCreativesPerformance = React.useCallback(() =>
    client.getReportsCreatives({
      projectId: scope.projectId,
      onboardingId: scope.onboardingId || undefined,
      versionId: scope.versionId || undefined,
      alias: scope.alias,
      from: zonedDayBoundIso(scope.from, scope.tz),
      to: zonedDayBoundIso(scope.to, scope.tz),
      tz: scope.tz && scope.tz !== 'UTC' ? scope.tz : undefined,
      paidFilters: compactPaidFilters(paidFilters),
      view: 'performance',
      attributionModelId: attributionModelRef.current,
    }).then(result => result.data.creativePerformance ?? { rows: [] }), [client, scope, paidFilters]);
  const loadCampaignCreatives = React.useCallback((campaignId: string, cursor?: string) =>
    client.getReportsCreativesDrilldown({
      projectId: scope.projectId,
      onboardingId: scope.onboardingId || undefined,
      versionId: scope.versionId || undefined,
      alias: scope.alias,
      from: zonedDayBoundIso(scope.from, scope.tz),
      to: zonedDayBoundIso(scope.to, scope.tz),
      tz: scope.tz && scope.tz !== 'UTC' ? scope.tz : undefined,
      paidFilters: compactPaidFilters(paidFilters),
      limit: 25,
      campaignId,
      cursor,
    }).then(result => result.data), [client, scope, paidFilters]);

  // W-R4-2 (DISC-AC-142b): the prior UPGRADE lane. An un-gated URL-explicit
  // first paint fetched without compare=prior (coverage was unknown); once
  // coverage settles and confirms a covered adjacent-left window, the cheap
  // split summary refetch fills priorSummary — the monolith never re-runs.
  // Self-heals a server-side prior failure on the normal path too.
  React.useEffect(() => {
    if (servedFromCache || loadingDashboard || !dashboardResult || !coverage) return undefined;
    if (dashboardResult.data.priorSummary) return undefined;
    if (!hasRequiredDashboardScope(scope)) return undefined;
    if (!hasPriorCoverageFor(coverage, scope.from, scope.to)) return undefined;
    const controller = new AbortController();
    client.getReportsSummary({
      projectId: scope.projectId,
      onboardingId: scope.onboardingId || undefined,
      versionId: scope.versionId || undefined,
      alias: scope.alias,
      from: zonedDayBoundIso(scope.from, scope.tz),
      to: zonedDayBoundIso(scope.to, scope.tz),
      tz: scope.tz && scope.tz !== 'UTC' ? scope.tz : undefined,
      // Parity of inputs (Session-3 lesson 1): the upgrade request mirrors the
      // load batch's request envelope exactly — compare is the ONLY delta.
      target: scope.target,
      paidFilters: compactPaidFilters(paidFilters),
      compare: 'prior',
      limit: 25,
      signal: controller.signal,
    })
      .then(result => {
        if (controller.signal.aborted || !result.data.priorSummary) return;
        setDashboardResult(current => (current && !current.data.priorSummary
          ? {
            ...current,
            data: {
              ...current.data,
              priorSummary: result.data.priorSummary,
              ...(result.data.priorWindow ? { priorWindow: result.data.priorWindow } : {}),
            },
          }
          : current));
      })
      // Prior comparison is additive — a failed upgrade keeps the labeled
      // half-window fallback delta, never an error state.
      .catch(() => undefined);
    return () => controller.abort();
  }, [
    client,
    servedFromCache,
    loadingDashboard,
    dashboardResult,
    coverage,
    scope,
    paidFilters,
  ]);

  // D-F1: every successful FRESH load overwrites the single per-project entry
  // — the next visit opens instantly on the last viewed scope.
  // W-R5: the merged split payload writes only once every lane settled —
  // a streaming partial merge must never become the cached snapshot.
  React.useEffect(() => {
    if (servedFromCache || loadingDashboard || !scope.projectId || !dashboardResult) return;
    writeInsightsCache<ReportsCachePayloads>(
      reportsCacheKey(scope.projectId),
      reportsScopeKey(scope, paidFilters),
      {
        scope,
        paidFilters,
        dashboard: dashboardResult,
        decisions: abDecisionResult,
        coverage,
      },
    );
  }, [servedFromCache, loadingDashboard, scope, paidFilters, dashboardResult, abDecisionResult, coverage]);

  React.useEffect(() => {
    let active = true;

    async function loadScopeOptions() {
      if (!projectId) {
        setOnboardings([]);
        setWebPlacements([]);
        setAllPlacements([]);
        return;
      }

      setLoadingOnboardings(true);
      setLoadingAliases(true);
      setScopeSelectorError(null);
      try {
        const [onboardingList, placementList] = await Promise.all([
          onboardingService.listOnboardings(projectId),
          webPlacementService.list(projectId),
        ]);
        const v2Only = onboardingList.filter(onboarding => onboarding.screenGraphType === 'v2');
        if (!active) return;
        setOnboardings(v2Only);
        setAllPlacements(placementList);
        setWebPlacements(
          placementList.filter(placement => placement.type === 'single' && Boolean(placement.alias)),
        );
      } catch (error) {
        if (!active) return;
        setOnboardings([]);
        setWebPlacements([]);
        setAllPlacements([]);
        setScopeSelectorError(error instanceof Error ? error.message : 'Failed to load onboardings');
      } finally {
        if (active) {
          setLoadingOnboardings(false);
          setLoadingAliases(false);
        }
      }
    }

    loadScopeOptions();

    return () => {
      active = false;
    };
  }, [projectId, onboardingService, webPlacementService]);

  React.useEffect(() => {
    let active = true;

    async function loadVersions() {
      if (!projectId || !draftScope.onboardingId) {
        setVersions([]);
        return;
      }

      setLoadingVersions(true);
      setScopeSelectorError(null);
      try {
        const list = await onboardingService.listVersions(projectId, draftScope.onboardingId);
        if (!active) return;
        setVersions(list);
        setDraftScope(current => {
          if (current.onboardingId !== draftScope.onboardingId) {
            return current;
          }
          if (current.versionId && list.some(version => version.id === current.versionId)) {
            return current;
          }
          if (current.versionId) {
            return current;
          }
          return {
            ...current,
            versionId: '',
          };
        });
      } catch (error) {
        if (!active) return;
        setVersions([]);
        setScopeSelectorError(error instanceof Error ? error.message : 'Failed to load onboarding versions');
      } finally {
        if (active) {
          setLoadingVersions(false);
        }
      }
    }

    loadVersions();

    return () => {
      active = false;
    };
  }, [projectId, draftScope.onboardingId, onboardingService, onboardings]);

  const updateDateScope = (key: 'from' | 'to') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = dateTimeInputToIso(event.target.value);
    setActiveDatePreset(null);
    setDraftScope(current => ({ ...current, [key]: nextValue }));
  };

  // B8 / contract A3 / PE-6: `to` snaps to the freshest data-bearing moment;
  // `from` counts the preset length back from it — never from "now". Coverage
  // (real covered days) outranks the envelope, which the server synthesizes.
  const coverageAnchorIso = coverage?.maxDate
    ? new Date(Date.parse(`${coverage.maxDate}T00:00:00.000Z`) + DAY_MS).toISOString()
    : null;
  const freshnessAnchorIso = coverageAnchorIso
    || dashboardResult?.freshness?.aggregateCoversThrough
    || dashboardResult?.asOf
    || null;

  const applyDatePreset = (days: 7 | 28) => {
    const toIso = freshnessAnchorIso ?? new Date().toISOString();
    const fromIso = new Date(new Date(toIso).getTime() - days * DAY_MS).toISOString();
    setActiveDatePreset(days);
    setDraftScope(current => ({ ...current, from: fromIso, to: toIso }));
  };

  // PE-6 (D-P2): the ready-period picker — rolling windows of the active
  // preset length; anything else displays as «Custom range».
  const readyPeriodOptions = React.useMemo(
    () => buildReadyPeriodOptions(coverage, activeDatePreset ?? 7),
    [coverage, activeDatePreset],
  );
  const selectedPeriodValue = readyPeriodOptions
    .find(option => option.from === draftScope.from && option.to === draftScope.to)?.value
    ?? 'custom';

  const handlePeriodSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const option = readyPeriodOptions.find(candidate => candidate.value === event.target.value);
    if (!option) return; // «Custom range…» — the From/To inputs below own the draft.
    setDraftScope(current => ({ ...current, from: option.from, to: option.to }));
  };

  // PE-6 (D-P4): the ONLY path from the form to the fetch input.
  const scopePending = SCOPE_COMPARE_KEYS.some(key => draftScope[key] !== scope[key]);
  const paidFiltersPending = PAID_FILTER_KEYS.some(key => draftPaidFilters[key] !== paidFilters[key]);
  const hasPendingChanges = scopePending || paidFiltersPending;
  // The local paid-table preview is equivalent to a server recompute only
  // while no APPLIED filter is being changed to a different value.
  const paidPreviewValid = PAID_FILTER_KEYS.every(key => {
    const applied = paidFilters[key].trim();
    return !applied || applied === draftPaidFilters[key].trim();
  });

  const handleApplyScope = () => {
    setScope(draftScope);
    setPaidFilters(draftPaidFilters);
    setRefreshResult(null);
    setRefreshError(null);
    // D-F1: Apply is an explicit action — always fetches fresh, past the cache.
    setServedFromCache(false);
  };

  const handleOnboardingSelect = (_event: React.SyntheticEvent, selectedOnboarding: Onboarding | null) => {
    const onboardingId = selectedOnboarding?.id || '';
    setDraftScope(current => ({
      ...current,
      onboardingId,
      versionId: '',
      alias: '',
    }));
    setVersions([]);
    setRefreshResult(null);
    setRefreshError(null);
  };

  const handleVersionSelect = (_event: React.SyntheticEvent, selectedVersion: VersionSelectOption | null) => {
    setDraftScope(current => ({ ...current, versionId: selectedVersion?.id || '', alias: '' }));
    setRefreshResult(null);
    setRefreshError(null);
  };

  const handleAliasSelect = (_event: React.SyntheticEvent, selectedPlacement: WebPlacement | null) => {
    setDraftScope(current => ({
      ...current,
      onboardingId: '',
      versionId: '',
      alias: selectedPlacement?.alias || '',
    }));
    setRefreshResult(null);
    setRefreshError(null);
  };

  // L5 (analytics-versions-and-publish-flow): scope-navigating drill-downs
  // open the drilled report in a NEW TAB with explicit scope params + the
  // APPLIED window; the current tab keeps its scope and data untouched.
  // (A/B variant and «Versions (N)» expanders stay local in-place toggles.)
  const openDrilledReportInNewTab = React.useCallback(
    (drill: { alias?: string; onboardingId?: string; versionId?: string }) => {
      const params = new URLSearchParams();
      if (drill.onboardingId) params.set('onboardingId', drill.onboardingId);
      if (drill.versionId) params.set('versionId', drill.versionId);
      if (drill.alias && drill.alias !== '(no alias)') params.set('alias', drill.alias);
      if (scope.from) params.set('from', scope.from);
      if (scope.to) params.set('to', scope.to);
      if (scope.tz && scope.tz !== 'UTC') params.set('tz', scope.tz);
      if (scope.target && scope.target !== 'reports') params.set('target', scope.target);
      // F4: the drilled report is a NEW page load, so the counting unit has to
      // travel with it exactly like tz/target above — otherwise drilling down
      // from a visits view lands the reader in people without saying so.
      applyFunnelUnitParam(params, funnelUnit);
      const paidParamNames: Record<PaidFilterKey, string> = {
        campaign: 'paidCampaign',
        metaAdAccountId: 'metaAdAccountId',
        metaCampaignId: 'metaCampaignId',
        metaAdsetId: 'metaAdsetId',
        metaAdId: 'metaAdId',
        metaCreativeId: 'metaCreativeId',
        matchQuality: 'matchQuality',
      };
      (Object.keys(paidParamNames) as PaidFilterKey[]).forEach(key => {
        if (paidFilters[key]) params.set(paidParamNames[key], paidFilters[key]);
      });
      window.open(`/project/${projectId}/reports?${params.toString()}`, '_blank', 'noopener');
    },
    [funnelUnit, paidFilters, projectId, scope.from, scope.target, scope.to, scope.tz],
  );

  const handleAliasDrilldown = (alias: string) => {
    openDrilledReportInNewTab({ alias });
  };

  const handleOnboardingVersionDrilldown = (nextScope: { alias: string; onboardingId: string; versionId?: string }) => {
    openDrilledReportInNewTab(nextScope);
  };

  const handleTargetSelect = (_event: React.SyntheticEvent, selectedTarget: string | null) => {
    setDraftScope(current => ({ ...current, target: selectedTarget || 'reports' }));
    setRefreshResult(null);
    setRefreshError(null);
  };

  const setPaidFilterValue = (key: PaidFilterKey) => (value: string) => {
    setDraftPaidFilters(current => ({ ...current, [key]: value }));
  };

  // Draft paid filters narrow the loaded paid tables INSTANTLY — no refetch.
  // The preview runs only while it is guaranteed equivalent to a server
  // recompute: every APPLIED filter field is empty or unchanged in the draft
  // (an applied filter already narrowed the payload rows, and only Apply can
  // widen them again).
  const displayDashboardData = React.useMemo(() => {
    const data = dashboardResult?.data ?? null;
    if (!data) return null;
    const draftActive = PAID_FILTER_KEYS.some(key => draftPaidFilters[key].trim() !== '');
    if (!draftActive || !paidPreviewValid) return data;
    // DISC-AC-138: every paid surface narrows through the same contract.
    // W-R6: campaign economics is the only paid table left on this page —
    // the CD saved views carry their own facet filters over the same contract.
    return {
      ...data,
      campaignEconomics: data.campaignEconomics
        ? applyPaidFacetFilters('campaignEconomics', data.campaignEconomics, draftPaidFilters)
        : data.campaignEconomics,
    };
  }, [dashboardResult?.data, draftPaidFilters, paidPreviewValid]);

  const handleMatchQualitySelect = (_event: React.SyntheticEvent, selectedQuality: string | null) => {
    setDraftPaidFilters(current => ({ ...current, matchQuality: selectedQuality || '' }));
  };

  const handleRefresh = async () => {
    if (!hasRequiredRefreshScope(scope)) return;
    setRefreshing(true);
    setRefreshError(null);
    setRefreshResult(null);

    try {
      const result = await client.refreshReports({
        projectId: scope.projectId,
        onboardingId: scope.onboardingId,
        versionId: scope.versionId,
        from: scope.from,
        to: scope.to,
        target: scope.target || 'reports',
        dryRun,
      });
      setRefreshResult(result);
      setServedFromCache(false);
      setDashboardReloadKey(current => current + 1);
    } catch (error) {
      setRefreshError(error instanceof Error ? error.message : 'Report refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const canRefresh = hasRequiredRefreshScope(scope) && !refreshing;
  // PE-6 (D-P4): every form control reads/writes the DRAFT.
  const selectedOnboarding = React.useMemo(
    () => onboardings.find(onboarding => onboarding.id === draftScope.onboardingId) || null,
    [onboardings, draftScope.onboardingId],
  );
  const selectedVersion = React.useMemo(
    () => versions.find(version => version.id === draftScope.versionId) || null,
    [versions, draftScope.versionId],
  );
  const versionOptions = React.useMemo<VersionSelectOption[]>(
    () => (draftScope.onboardingId ? [ALL_VERSIONS_OPTION, ...versions] : []),
    [draftScope.onboardingId, versions],
  );
  const selectedVersionOption = draftScope.onboardingId && !draftScope.versionId
    ? ALL_VERSIONS_OPTION
    : selectedVersion;
  /**
   * P1-10 (design review): the APPLIED scope by NAME. Two problems it fixes:
   * a version-scoped page was byte-identical to the project-wide one above the
   * fold (A4 moved the scope chips into the bottom accordion), and those chips
   * printed the raw document ids — `Version: wuRHbvfVJuGIMpvChdwL` — the exact
   * id D6 evicted from the table. `onboardings` / `versions` already carry the
   * names; the id stays as the fallback so a scope is never unidentifiable.
   */
  const appliedOnboardingName = React.useMemo(
    () => onboardings.find(onboarding => onboarding.id === scope.onboardingId)?.name?.trim() || undefined,
    [onboardings, scope.onboardingId],
  );
  const appliedVersionName = React.useMemo(
    () => versions.find(version => version.id === scope.versionId)?.name?.trim() || undefined,
    [versions, scope.versionId],
  );
  /** The header's scope line — absent when the report IS the whole project. */
  const appliedScopeLabel = React.useMemo(() => {
    const parts = [
      scope.onboardingId ? appliedOnboardingName ?? scope.onboardingId : undefined,
      scope.versionId ? appliedVersionName ?? scope.versionId : undefined,
      scope.alias ? `placement ${scope.alias}` : undefined,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
  }, [scope.onboardingId, scope.versionId, scope.alias, appliedOnboardingName, appliedVersionName]);

  const aliasOptions = webPlacements;
  const selectedAliasPlacement = React.useMemo(
    () => aliasOptions.find(placement => placement.alias === draftScope.alias) || null,
    [aliasOptions, draftScope.alias],
  );
  const selectedTarget = React.useMemo(
    () => REPORT_REFRESH_TARGET_OPTIONS.find(target => target === draftScope.target) || null,
    [draftScope.target],
  );
  const selectedMatchQuality = React.useMemo(
    () => MATCH_QUALITY_OPTIONS.find(matchQuality => matchQuality === draftPaidFilters.matchQuality) || null,
    [draftPaidFilters.matchQuality],
  );
  // Paid filter dropdowns list only values seen in the loaded window, labeled
  // with campaign context so raw Meta ids stay recognizable.
  // W-R6 re-home decision (PRE-021 §1f/§2f): options build from the
  // campaignEconomics rows ONLY — the diagnostics section no longer rides the
  // dashboard payload; diagnostics-only creatives narrow via the CD saved
  // view's own filters (free-text matches regardless of option presence).
  const paidFilterOptions = React.useMemo(() => {
    const rows: FunnelCampaignEconomicsRow[] = [
      ...(dashboardResult?.data?.campaignEconomics ?? []),
    ];
    const collect = (
      getValue: (row: FunnelCampaignEconomicsRow) => string | undefined,
      getHint: (row: FunnelCampaignEconomicsRow) => string,
    ): PaidFilterOptionSet => {
      const hints = new Map<string, string>();
      for (const row of rows) {
        const optionValue = (getValue(row) || '').trim();
        if (!optionValue || hints.has(optionValue)) continue;
        hints.set(optionValue, getHint(row));
      }
      return { values: [...hints.keys()], hints };
    };
    const campaignHint = (row: FunnelCampaignEconomicsRow) => {
      const parts = [`${row.sessions} sessions`];
      if (typeof row.spend === 'number') parts.push(`spend $${row.spend.toFixed(2)}`);
      return parts.join(' · ');
    };
    return {
      campaign: collect(row => row.campaign, campaignHint),
      metaAdAccountId: collect(row => row.metaAdAccountId, row => row.campaign),
      metaCampaignId: collect(row => row.metaCampaignId, row => row.campaign),
      metaAdsetId: collect(row => row.metaAdsetId, row => row.campaign),
      metaAdId: collect(row => row.metaAdId, row => row.campaign),
      metaCreativeId: collect(row => row.metaCreativeId, row => row.metaCreativeName || row.campaign),
    };
  }, [dashboardResult?.data]);
  const reportAvailability = React.useMemo(
    () => getFunnelReportAvailability(dashboardResult?.data),
    [dashboardResult?.data],
  );
  const scopeMode = getScopeMode(scope);
  // Reports parity: every Open → canvas report deep-link carries the APPLIED
  // period so the canvas queries the same window the row was read from
  // (?insights=1 alone means all-time on the canvas side).
  const effectiveFrom = zonedDayBoundIso(scope.from, scope.tz);
  const effectiveTo = zonedDayBoundIso(scope.to, scope.tz);
  const reportWindowParams = (scope.from && scope.to
    ? `&from=${encodeURIComponent(effectiveFrom)}&to=${encodeURIComponent(effectiveTo)}`
    : '')
    // F2 (FT-AC-083): Open links also carry the page unit so the canvas
    // report renders the SAME counting unit the row was read in. The fragment
    // helper owns the "only the non-default unit travels" rule — before F4
    // this was a hand-written `people ? '&unit=people' : ''`, which would have
    // sent a VISITS reader into a people canvas the moment people became the
    // default.
    + funnelUnitQueryFragment(funnelUnit);
  // A3: what the header's date trigger says. Three parts, as in the mock:
  // the preset name, the window, and its length + the timezone days are cut in.
  const windowDays = windowLengthDays(scope.from, scope.to);
  const dateTriggerPresetLabel = activeDatePreset === 7
    ? 'Latest 7 days'
    : activeDatePreset === 28 ? 'Latest 28 days' : 'Custom range';
  const dateTriggerRangeLabel = formatWindowRangeLabel(scope.from, scope.to);
  const dateTriggerLengthLabel = `${pluralize(windowDays, 'day')} · ${scope.tz || 'UTC'}`;
  // PE6-AC-006: the deltas compare against a SERVED prior window. Without one
  // they fall back to the half-window basis, and this caption would misstate the
  // comparison — so it renders only when `priorWindow` actually came back.
  const servedPriorWindow = dashboardResult?.data?.priorWindow;
  // P1-9 (design review): the header used to print «vs previous 7 days»
  // whenever a prior window was SERVED, while the band renders a change row
  // only for the three gates the RAW summary can back — and none at all in
  // people mode. The promise and the page disagreed. The caption now waits for
  // the same condition the change rows do, so the header never advertises a
  // comparison the reader will not find.
  const comparisonCaption = servedPriorWindow && funnelUnit !== 'people'
    ? `vs previous ${pluralize(windowLengthDays(servedPriorWindow.from, servedPriorWindow.to), 'day')}`
    : null;
  const hasABDecisionData = Boolean(abDecisionResult?.data.decisions.length);
  const hasAnyReportData = hasAnyFunnelReportData(dashboardResult?.data) || hasABDecisionData;
  // PE-1 (handoff §A8): one page-level banner when sections failed and NOTHING
  // has data (per-section cards cover partial failures inside the dashboard).
  const dashboardSectionErrors = dashboardResult?.data?.sectionErrors ?? [];
  const sectionFailureCopy = dashboardSectionErrors.length > 0 && !hasAnyReportData
    ? describeReportSectionErrors(dashboardSectionErrors)
    : null;
  // R4-1 (B9): report-level trust banner — data SURVIVED but should be read
  // with caution (critical DQ or partly failed sections). Complements, never
  // duplicates, dashboardSectionFailure (which owns the all-failed/no-data case).
  const summaryQualitySeverity = (dashboardResult?.data?.summary.dataQualitySeverity || '').toLowerCase();
  const trustBanner = hasAnyReportData && (summaryQualitySeverity === 'critical' || dashboardSectionErrors.length > 0)
    ? {
        severity: summaryQualitySeverity === 'critical' ? ('error' as const) : ('warning' as const),
        message: [
          summaryQualitySeverity === 'critical'
            ? 'Data quality is critical for this window — do not use these metrics for decisions.'
            : null,
          dashboardSectionErrors.length > 0
            ? `${dashboardSectionErrors.length} report ${dashboardSectionErrors.length === 1 ? 'section' : 'sections'} failed to load; the affected cards are marked below.`
            : null,
        ].filter(Boolean).join(' '),
      }
    : null;
  const projectSyncStatus = React.useMemo(
    () => formatProjectSyncStatus(dashboardResult?.data?.summary.latestMaterializedAt, clockNowMs),
    [clockNowMs, dashboardResult?.data?.summary.latestMaterializedAt],
  );

  // FU part E (D-F2): A/B placements by alias — each variant resolves to the
  // CHILD single placement's funnel/version config (the variant doc itself
  // carries only the webPlacementId reference).
  const abPlacementsByAlias = React.useMemo(() => {
    const map: Record<string, AbPlacementReportInfo> = {};
    for (const placement of allPlacements) {
      if (placement.type !== 'ab_test' || !placement.alias) continue;
      const variants = (placement.variants ?? []).map(variant => {
        const child = allPlacements.find(candidate => candidate.id === variant.webPlacementId);
        return {
          variantId: variant.id,
          name: variant.name || variant.webPlacementName || variant.id,
          funnelId: child?.config?.funnelId,
          versionId: child?.config?.versionId,
        };
      });
      map[placement.alias] = { placementId: placement.id, variants };
    }
    return map;
  }, [allPlacements]);

  const insightNotifications = useInsightNotifications({ projectId, enabled: Boolean(projectId) });
  const resolveLink = React.useCallback(
    (link: Parameters<typeof resolveNotificationLink>[1]) => resolveNotificationLink(projectId, link),
    [projectId],
  );

  return (
    // F2: one unit for EVERY funnel metric group on the page (FT-AC-083) —
    // the dashboard reads it from context, no prop threading.
    <FunnelUnitProvider unit={funnelUnit}>
    <Box data-testid={FUNNEL_REPORTS_TEST_IDS.page} sx={{ p: { xs: 2, md: 3 }, maxWidth: 1360, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* A1/A3 (IMPLEMENTATION_SPEC): ONE header row — title, the date trigger
            with its own popover, the comparison statement, the QA menu and the
            bell. The subtitle is gone: the title plus the first fold say it. */}
        <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Reports
            </Typography>
            {/* P1-10: WHAT the numbers belong to, above the fold. A4 moved the
                scope chips into the bottom accordion, which left a version
                report looking exactly like a project report with odd numbers.
                Absent when the report IS the whole project — the title already
                says that. */}
            {appliedScopeLabel && (
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid={FUNNEL_REPORTS_TEST_IDS.reportsScopeSummary}
              >
                {appliedScopeLabel}
              </Typography>
            )}
          </Stack>
          <Box sx={{ flex: 1 }} />
          {/* A3: the date block must not shrink below its own trigger width. */}
          <Button
            data-testid={FUNNEL_REPORTS_TEST_IDS.reportsDateTrigger}
            variant="outlined"
            size="small"
            aria-haspopup="dialog"
            aria-expanded={Boolean(dateMenuAnchor)}
            onClick={event => setDateMenuAnchor(event.currentTarget)}
            startIcon={<CalendarTodayIcon fontSize="small" />}
            endIcon={<ExpandMoreIcon fontSize="small" />}
            sx={{ flex: 'none', textTransform: 'none', color: 'text.primary', borderColor: 'divider' }}
          >
            <Stack direction="row" spacing={1} alignItems="baseline" useFlexGap flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">{dateTriggerPresetLabel}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{dateTriggerRangeLabel}</Typography>
              <Typography variant="caption" color="text.secondary">{dateTriggerLengthLabel}</Typography>
            </Stack>
          </Button>
          {/* Only when a prior window was actually served — otherwise the deltas
              fall back to the half-window basis and this line would be a claim
              the page cannot back (PE6-AC-006). */}
          {comparisonCaption && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={FUNNEL_REPORTS_TEST_IDS.reportsComparisonCaption}
              sx={{ flex: 'none' }}
            >
              {comparisonCaption}
            </Typography>
          )}
          {(canUseRefresh || showQaControls) && (
            <IconButton
              data-testid={FUNNEL_REPORTS_TEST_IDS.qaMenuButton}
              aria-label="Reports refresh and QA menu"
              size="small"
              onClick={event => setQaMenuAnchor(event.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
          <NotificationCenter
            notifications={insightNotifications.notifications}
            unreadCount={insightNotifications.unreadCount}
            error={insightNotifications.error}
            markRead={insightNotifications.markRead}
            markAllRead={insightNotifications.markAllRead}
            resolveLink={resolveLink}
            onNavigate={(path) => navigate(path)}
          />
        </Stack>

        {/* A3: the date controls keep every test id and the Apply gate — they
            only move off the page body into the trigger's popover. */}
        <Popover
          open={Boolean(dateMenuAnchor)}
          anchorEl={dateMenuAnchor}
          onClose={() => setDateMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Stack spacing={2} sx={{ p: 2, width: 560 }} data-testid={FUNNEL_REPORTS_TEST_IDS.reportsDatePopover}>
        {/* R1-2 (B8): presets in front of the raw pickers, anchored to data freshness.
            PE-6 (D-P4): they edit the DRAFT; the Apply button owns the fetch. */}
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Chip
            data-testid={FUNNEL_REPORTS_TEST_IDS.datePreset('7d')}
            label="Last 7 days"
            size="small"
            color={activeDatePreset === 7 ? 'primary' : 'default'}
            onClick={() => applyDatePreset(7)}
          />
          <Chip
            data-testid={FUNNEL_REPORTS_TEST_IDS.datePreset('28d')}
            label="Last 28 days"
            size="small"
            color={activeDatePreset === 28 ? 'primary' : 'default'}
            onClick={() => applyDatePreset(28)}
          />
          {readyPeriodOptions.length > 0 && (
            <TextField
              data-testid={FUNNEL_REPORTS_TEST_IDS.reportsPeriodSelect}
              select
              size="small"
              label="Period (ready to view)"
              value={selectedPeriodValue}
              onChange={handlePeriodSelect}
              sx={{ minWidth: 250 }}
            >
              {readyPeriodOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
              <MenuItem value="custom">Custom range…</MenuItem>
            </TextField>
          )}
          <Autocomplete
            data-testid={FUNNEL_REPORTS_TEST_IDS.reportsTimezoneSelect}
            size="small"
            options={timeZoneOptions}
            value={draftScope.tz || 'UTC'}
            onChange={(_event, zone) => setDraftScope(current => ({ ...current, tz: zone || 'UTC' }))}
            disableClearable
            getOptionLabel={zone => zone === 'UTC'
              ? 'UTC (default)'
              : `${zone}${zone === browserTimeZone ? ' · your browser' : ''} (${zoneOffsetLabel(zone)})`}
            sx={{ minWidth: 250 }}
            renderInput={inputParams => <TextField {...inputParams} label="Timezone" />}
          />
          {/* A5 (owner round 4, 07.10): the People | Visits toggle is HIDDEN, not
              removed. The mode, `FunnelUnitProvider` and the `unit` query param
              all stay, and every visible label keeps its unit noun (designer.md
              D5). `handleFunnelUnitChange` remains the only writer of the mode,
              so the control can come back without a rewrite. Hub-side unit
              assertions drive the `unit` URL param instead (plan §3 S3). */}
          <Box sx={{ flex: 1 }} />
          <Badge color="warning" variant="dot" invisible={!hasPendingChanges}>
            <Button
              data-testid={FUNNEL_REPORTS_TEST_IDS.reportsApply}
              size="small"
              variant="contained"
              disabled={!hasPendingChanges}
              onClick={handleApplyScope}
            >
              Apply
            </Button>
          </Badge>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
          {coverage?.minDate && coverage.maxDate ? (
            <Typography variant="caption" color="text.secondary" data-testid={FUNNEL_REPORTS_TEST_IDS.reportsCoverageCaption}>
              {`Data available ${formatCoverageDate(coverage.minDate)} – ${formatCoverageDate(coverage.maxDate)} · periods count back from the latest covered day, not from now.`}
              {scope.tz && scope.tz !== 'UTC'
                ? ` Days start at local midnight in ${scope.tz} (${zoneOffsetLabel(scope.tz)}); daily trend charts keep UTC day buckets.`
                : ''}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" data-testid={FUNNEL_REPORTS_TEST_IDS.freshnessCaption}>
              {freshnessAnchorIso
                ? `Data through ${formatUtcDateTime(freshnessAnchorIso)} — presets count back from the freshest materialized data, not from now.`
                : 'Presets anchor to the freshest materialized data once reports load.'}
            </Typography>
          )}
          {hasPendingChanges && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ fontWeight: 600 }}
              data-testid={FUNNEL_REPORTS_TEST_IDS.reportsPendingHint}
            >
              {!scopePending && paidFiltersPending && paidPreviewValid
                ? 'Paid tables are filtered live — press Apply to save the filters into the report scope.'
                : 'Pending changes — press Apply to reload the report.'}
            </Typography>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            data-testid={FUNNEL_REPORTS_TEST_IDS.fromInput}
            label="From date/time"
            type="datetime-local"
            value={dateTimeInputFromIso(draftScope.from)}
            onChange={updateDateScope('from')}
            fullWidth
            size="small"
            helperText="UTC"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            data-testid={FUNNEL_REPORTS_TEST_IDS.toInput}
            label="To date/time"
            type="datetime-local"
            value={dateTimeInputFromIso(draftScope.to)}
            onChange={updateDateScope('to')}
            fullWidth
            size="small"
            helperText="UTC, end exclusive"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
          </Stack>
        </Popover>

        <Popover
          open={Boolean(qaMenuAnchor)}
          anchorEl={qaMenuAnchor}
          onClose={() => setQaMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Stack spacing={2} sx={{ p: 2, width: 380 }} data-testid={FUNNEL_REPORTS_TEST_IDS.qaMenu}>
            {canUseRefresh && (
              <>
                <Button
                  data-testid={FUNNEL_REPORTS_TEST_IDS.refreshButton}
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  disabled={!canRefresh}
                  onClick={handleRefresh}
                >
                  Refresh report data
                </Button>
                <Typography
                  data-testid={FUNNEL_REPORTS_TEST_IDS.projectSyncStatus}
                  variant="caption"
                  color="text.secondary"
                >
                  {projectSyncStatus}
                </Typography>
                {!hasRequiredRefreshScope(scope) && (
                  <Alert severity="info" data-testid={FUNNEL_REPORTS_TEST_IDS.scopeSelectorStatus}>
                    Existing materialized reports are shown for the selected scope and date range. Manual refresh is scoped to one onboarding version; open a flow from the active-placement table or choose onboarding/version to refresh that slice.
                  </Alert>
                )}
              </>
            )}
            {canUseRefresh && showQaControls && <Divider />}
            {showQaControls && (
              <>
                <Autocomplete
                  data-testid={FUNNEL_REPORTS_TEST_IDS.targetInput}
                  options={REPORT_REFRESH_TARGET_OPTIONS}
                  value={selectedTarget}
                  onChange={handleTargetSelect}
                  fullWidth
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Target"
                      size="small"
                      helperText={draftScope.target && !selectedTarget ? `Selected target: ${draftScope.target}` : undefined}
                    />
                  )}
                />
                <FormControlLabel
                  data-testid={FUNNEL_REPORTS_TEST_IDS.dryRunToggle}
                  control={<Switch checked={dryRun} onChange={(_, checked) => setDryRun(checked)} />}
                  label="Dry run"
                />
                <FormControlLabel
                  data-testid={FUNNEL_REPORTS_TEST_IDS.showEmptyReportsToggle}
                  control={<Switch checked={showEmptyReports} onChange={(_, checked) => setShowEmptyReports(checked)} />}
                  label="Show empty report sections"
                />
                <Divider />
                {/* R1-5 (D-Q4): BQ provenance + external BI links — QA drawer, not the user surface. */}
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  BQ provenance &amp; BI links
                </Typography>
                <Stack spacing={0.75} data-testid={FUNNEL_REPORTS_TEST_IDS.qaProvenance}>
                  {REPORT_SURFACES.map(surface => {
                    const reportUrl = buildReportSurfaceUrl(reportBaseUrl, surface, scope);
                    return (
                      <Stack key={surface.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" noWrap>{surface.title}</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {/* DESIGN.md Caption-Floor Rule: 0.75 rem is the floor —
                              0.62 rem existed only to fit a long BQ view name into
                              the 380 px popover. `noWrap` on the title beside it
                              absorbs the extra width instead. */}
                          <Chip
                            size="small"
                            label={surface.sourceView}
                            sx={{ fontSize: '0.75rem', maxWidth: 170, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                          />
                          {reportUrl && (
                            <Button
                              size="small"
                              href={reportUrl}
                              target="_blank"
                              rel="noreferrer"
                              endIcon={<OpenInNewIcon />}
                            >
                              BI
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </>
            )}
          </Stack>
        </Popover>
        {refreshing && <LinearProgress aria-label="Refreshing report data" />}
        {refreshError && (
          <Alert severity="error" data-testid={FUNNEL_REPORTS_TEST_IDS.refreshStatus}>
            {refreshError}
          </Alert>
        )}
        {refreshResult && (
          <Alert severity="success" data-testid={FUNNEL_REPORTS_TEST_IDS.refreshStatus}>
            Report data refreshed through {formatUtc(refreshResult.data.freshness.effectiveDataThrough)}.
          </Alert>
        )}
        {refreshResult && (
          <Box data-testid={FUNNEL_REPORTS_TEST_IDS.refreshSummary} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {refreshResult.data.mode} | {pluralize(refreshResult.data.jobs.length, 'job')} | {refreshResult.data.totalBytesProcessedHuman} | {pluralize(refreshResult.data.refreshedTables.length, 'table')}
          </Box>
        )}

        {servedFromCache && initialCache && (
          <Alert
            data-testid={FUNNEL_REPORTS_TEST_IDS.cachedBanner}
            severity={isCacheStale(initialCache.savedAt, clockNowMs) ? 'warning' : 'info'}
            action={(
              <Button
                data-testid={FUNNEL_REPORTS_TEST_IDS.cachedRefresh}
                color="inherit"
                size="small"
                onClick={() => setServedFromCache(false)}
              >
                Refresh now
              </Button>
            )}
          >
            {`Showing saved report from ${new Date(initialCache.savedAt).toLocaleString()}. Fresh data loads only when you refresh or apply changes.`}
          </Alert>
        )}

        <NeedsActionStrip
          notifications={insightNotifications.notifications}
          resolveLink={resolveLink}
          onNavigate={(path) => navigate(path)}
          onMarkRead={(id) => void insightNotifications.markRead(id)}
        />

        {/* E: the attention strip is built FROM the admin-only sections and deep-links
            into them — it ships behind the same gate (owner call 07.10, the same
            call that put «Needs a decision» behind it). */}
        {canSeeBlock('attention-strip') && (
        <ReportAttentionStrip
          dashboard={dashboardResult?.data}
          decisions={abDecisionResult?.data.decisions ?? []}
          loaded={Boolean(dashboardResult)}
          actions={{
            // R5-4: SRM lands on the Experiments hub where the card header
            // carries the «SRM — split broken» badge (R3-2).
            openExperiments: () => navigate(`/project/${projectId}/experiments`),
            openDecisions: () => scrollToTestId(FUNNEL_REPORTS_TEST_IDS.chart('ab-test-decisions')),
          }}
        />
        )}


        {loadingDashboard && <LinearProgress aria-label="Loading report dashboard" />}
        {dashboardError && (
          <Alert severity={dashboardError.severity} data-testid={FUNNEL_REPORTS_TEST_IDS.dashboardStatus}>
            {dashboardError.headline}
            {dashboardError.techDetails && (
              <Box component="details" data-testid={FUNNEL_REPORTS_TEST_IDS.dashboardErrorDetails} sx={{ mt: 1 }}>
                <Box component="summary" sx={{ cursor: 'pointer' }}>{TECH_DETAILS_LABEL}</Box>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-word' }}>
                  {dashboardError.techDetails}
                </Typography>
              </Box>
            )}
          </Alert>
        )}
        {!dashboardError && dashboardResult?.warnings?.length ? (
          <Alert severity="warning" data-testid={FUNNEL_REPORTS_TEST_IDS.dashboardWarnings}>
            {dashboardResult.warnings.map(warning => warning.message).join(' ')}
          </Alert>
        ) : null}
        {trustBanner && (
          <Alert
            severity={trustBanner.severity}
            data-testid={FUNNEL_REPORTS_TEST_IDS.trustBanner}
            // R4-AC-001 keeps the banner for everyone — it qualifies the three
            // answers a member DOES see. Its CTA scrolls to «Warnings & data
            // trust», which is admin-only, so the action drops with the target
            // rather than pointing at nothing.
            action={canSeeBlock('warnings') ? (
              <Button color="inherit" size="small" onClick={() => scrollToTestId(FUNNEL_REPORTS_TEST_IDS.warningsSection)}>
                Data trust details
              </Button>
            ) : undefined}
          >
            {trustBanner.message}
          </Alert>
        )}
        {abDecisionError && (
          <Alert severity="warning" data-testid={FUNNEL_REPORTS_TEST_IDS.abDecisionStatus}>
            A/B decision report is unavailable: {abDecisionError}
          </Alert>
        )}
        {sectionFailureCopy && (
          <Alert severity={sectionFailureCopy.severity} data-testid={FUNNEL_REPORTS_TEST_IDS.dashboardSectionFailure}>
            {sectionFailureCopy.headline}
          </Alert>
        )}


        <FunnelReportsDashboard
          data={displayDashboardData}
          creativeDecisionsEnabled={creativeDecisionsEnabled}
          abTestDecisionReport={abDecisionResult?.data ?? null}
          showEmptySections={showEmptyReports}
          viewerRole={viewerRole}
          onShowVisits={handleShowVisits}
          scopeMode={scopeMode}
          projectId={projectId}
          scopeOnboardingId={scope.onboardingId || undefined}
          dataThrough={dashboardResult?.freshness?.effectiveDataThrough
            ?? dashboardResult?.freshness?.aggregateCoversThrough
            ?? undefined}
          decisionActionsFor={decisionActionsFor}
          loadCampaignCreatives={loadCampaignCreatives}
          trafficView={trafficView}
          onTrafficViewChange={handleTrafficViewChange}
          loadCreativesPerformance={loadCreativesPerformance}
          // W-R5 (DISC-AC-144): thresholds Save re-fetches — chips render the
          // server verdicts computed with the freshly saved thresholds.
          onVerdictThresholdsSaved={() => setDashboardReloadKey(key => key + 1)}
          // F3 (FT-AC-089): model switch → targeted acquisition-lane refetch.
          onAttributionModelChange={handleAttributionModelChange}
          attributionSwitchPending={attributionSwitchPending}
          onAliasSelect={handleAliasDrilldown}
          onOnboardingVersionSelect={handleOnboardingVersionDrilldown}
          onOpenPlacementEntity={({ placementId, alias }) => {
            // FU part E (D-F2): Open lands on the CONCRETE funnel in canvas
            // report mode — the placements list was a dead end. When the
            // placement doc is gone (deleted placements keep reporting
            // historical traffic), the report's own rows still know which
            // funnel served the alias — resolve from them before giving up.
            // Open in a NEW tab so the reports page the user is reading from
            // stays put (matches the A/B variant Open below).
            const placement = allPlacements.find(candidate => candidate.id === placementId)
              ?? allPlacements.find(candidate => candidate.alias === alias);
            const reportedRows = (dashboardResult?.data?.aliasOnboardingSummaries ?? [])
              .filter(row => row.alias === alias && row.onboardingId)
              .sort((a, b) => b.startedJourneys - a.startedJourneys);
            const funnelId = placement?.config?.funnelId ?? reportedRows[0]?.onboardingId;
            if (funnelId) {
              // AC-025 version rule: exactly 1 trafficked version of the
              // resolved funnel → that version; several → the top-starts
              // version. Never a silent latest/zero-traffic canvas.
              const funnelRows = reportedRows.filter(row => row.onboardingId === funnelId && row.versionId);
              const versionId = funnelRows[0]?.versionId ?? placement?.config?.versionId;
              const versionParam = versionId ? `&version=${encodeURIComponent(versionId)}` : '';
              window.open(`/project/${projectId}/onboarding-v2/${funnelId}?insights=1${versionParam}${reportWindowParams}`, '_blank', 'noopener');
              return;
            }
            window.open(`/project/${projectId}/web-placements${placementId ? `?placementId=${encodeURIComponent(placementId)}` : ''}`, '_blank', 'noopener');
          }}
          // S10 (D-M): the version row's «Sources» popup — pinned to its row.
          onOpenVersionSources={openVersionSources}
          onOpenOnboardingEntity={({ onboardingId, versionId }) => {
            // New tab: keep the reports page open behind the funnel canvas.
            // L2/AC-014: the row's versionId rides the deep link so the canvas
            // opens the version that actually carried the traffic.
            const versionParam = versionId ? `&version=${encodeURIComponent(versionId)}` : '';
            window.open(`/project/${projectId}/onboarding-v2/${onboardingId}?insights=1${versionParam}${reportWindowParams}`, '_blank', 'noopener');
          }}
          abPlacementsByAlias={abPlacementsByAlias}
          onOpenVariantReport={({ funnelId, versionId }) => {
            const versionParam = versionId ? `&version=${encodeURIComponent(versionId)}` : '';
            window.open(`/project/${projectId}/onboarding-v2/${funnelId}?insights=1${versionParam}${reportWindowParams}`, '_blank', 'noopener');
          }}
        />
        {/* R1-4 (D9): the audience/census research surface moves BELOW the overview,
            collapsed by default. R1-5 (D-Q4): the report-cards grid is deleted —
            BQ provenance + BI links live in the QA menu.
            E: observational research, no decision gates → admin-only. */}
        {canSeeBlock('audience') && (
        <Accordion
          disableGutters
          elevation={0}
          data-testid={FUNNEL_REPORTS_TEST_IDS.audienceSection}
          // A11y: one heading per card at h5 (MUI's own heading wrapper), the
          // visible title a span — see ChartSection for the same correction.
          slotProps={{ heading: { component: 'h5' } }}
          sx={{
            border: theme => `1px solid ${theme.palette.divider}`,
            // DESIGN.md surface radius is 16px (`borderRadius: 1` was 4px, and
            // MUI's rounded-accordion rule forces 4px anyway unless matched).
            borderRadius: 4,
            '&.MuiAccordion-rounded:first-of-type, &.MuiAccordion-rounded:last-of-type': {
              borderRadius: 4,
            },
            '&::before': { display: 'none' },
          }}
        >
          {/* C1 header treatment, shared with every other collapsible card. */}
          <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid={FUNNEL_REPORTS_TEST_IDS.audienceToggle} sx={{ px: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" component="span" sx={{ fontWeight: 500 }}>
                Audience — cohorts &amp; census
              </Typography>
              <Typography variant="caption" color="text.secondary">
                observational research surface
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <JourneyCohortCensusSection
                projectId={scope.projectId}
                onboardingId={scope.onboardingId || undefined}
                versionId={scope.versionId || undefined}
                from={scope.from}
                to={scope.to}
                hasLiveOnboardings={loadingOnboardings ? undefined : onboardings.length > 0}
              />
              <FunnelReportsBreakdownSection
                projectId={scope.projectId}
                onboardingId={scope.onboardingId || undefined}
                versionId={scope.versionId || undefined}
                from={scope.from}
                to={scope.to}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        )}

        {/* A4 (IMPLEMENTATION_SPEC): scope chips, the scope pickers and the paid
            filters are ONE «Advanced filters» accordion, and it is the last block
            on the page. The date controls stay in the header (A3). */}
        <Accordion
          disableGutters
          elevation={0}
          slotProps={{ heading: { component: 'h5' } }}
          sx={{
            border: theme => `1px solid ${theme.palette.divider}`,
            // DESIGN.md surface radius is 16px (`borderRadius: 1` was 4px, and
            // MUI's rounded-accordion rule forces 4px anyway unless matched).
            borderRadius: 4,
            '&.MuiAccordion-rounded:first-of-type, &.MuiAccordion-rounded:last-of-type': {
              borderRadius: 4,
            },
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid={FUNNEL_REPORTS_TEST_IDS.advancedFiltersToggle} sx={{ px: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="h6" component="span" sx={{ fontWeight: 500 }}>
                Advanced filters
              </Typography>
              <Chip
                size="small"
                label={activeAdvancedFilterCount}
                color={activeAdvancedFilterCount > 0 ? 'primary' : 'default'}
              />
              <Typography variant="caption" color="text.secondary">
                Scope, onboarding, version, alias, Meta ad account, campaign, ad set, ad, creative, match quality
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {/* A4: the scope pickers moved in here, so this panel needs its own
                  commit — otherwise an onboarding filter could only be applied by
                  opening the date popover. Both buttons commit the SAME draft. */}
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                {hasPendingChanges && (
                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                    Pending changes
                  </Typography>
                )}
                <Badge color="warning" variant="dot" invisible={!hasPendingChanges}>
                  <Button
                    data-testid={FUNNEL_REPORTS_TEST_IDS.reportsApplyFilters}
                    size="small"
                    variant="contained"
                    disabled={!hasPendingChanges}
                    onClick={handleApplyScope}
                  >
                    Apply
                  </Button>
                </Badge>
              </Stack>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Tooltip title={`Project id: ${scope.projectId || 'missing'}`}>
                  {/* R5-4 (D12 tail): no raw-id flash — a Skeleton holds the slot until the name hydrates. */}
                  <Chip
                    data-testid={FUNNEL_REPORTS_TEST_IDS.scopeProject}
                    label={projectName
                      ? `Project: ${projectName}`
                      : <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                          <span>Project:</span>
                          <Skeleton width={96} sx={{ display: 'inline-block' }} />
                        </Stack>}
                  />
                </Tooltip>
                {/* The id shows immediately and the NAME replaces it when the
                    list that carries it arrives. A Skeleton was tried here (the
                    project chip's pattern) and rejected: the version list does
                    not always settle for a drilled scope, and a chip stuck on
                    «Version:» with nothing after it is worse than a readable id
                    for a moment. The id is the honest placeholder — it IS the
                    scope. */}
                <Chip
                  data-testid={FUNNEL_REPORTS_TEST_IDS.scopeOnboarding}
                  label={`Onboarding: ${scope.onboardingId ? appliedOnboardingName ?? scope.onboardingId : 'all V2 onboardings'}`}
                />
                <Chip
                  data-testid={FUNNEL_REPORTS_TEST_IDS.scopeVersion}
                  label={`Version: ${scope.versionId ? appliedVersionName ?? scope.versionId : 'all versions'}`}
                />
                {scope.alias && <Chip data-testid={FUNNEL_REPORTS_TEST_IDS.scopeAlias} label={`Alias: ${scope.alias}`} />}
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Autocomplete
                  data-testid={FUNNEL_REPORTS_TEST_IDS.onboardingSelect}
                  options={onboardings}
                  value={selectedOnboarding}
                  onChange={handleOnboardingSelect}
                  getOptionLabel={formatOnboardingLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                  loading={loadingOnboardings}
                  disabled={loadingOnboardings}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Onboarding"
                      size="small"
                      helperText={draftScope.onboardingId && !selectedOnboarding ? `Selected onboarding: ${draftScope.onboardingId}` : undefined}
                    />
                  )}
                />
                <Autocomplete
                  data-testid={FUNNEL_REPORTS_TEST_IDS.versionSelect}
                  options={versionOptions}
                  value={selectedVersionOption}
                  onChange={handleVersionSelect}
                  getOptionLabel={formatVersionLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                  loading={loadingVersions}
                  disabled={!scope.onboardingId || loadingVersions}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Version"
                      size="small"
                      helperText={draftScope.versionId && !selectedVersion ? `Selected version: ${draftScope.versionId}` : undefined}
                    />
                  )}
                />
                <Autocomplete
                  data-testid={FUNNEL_REPORTS_TEST_IDS.aliasInput}
                  options={aliasOptions}
                  value={selectedAliasPlacement}
                  onChange={handleAliasSelect}
                  getOptionLabel={formatAliasLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                  loading={loadingAliases}
                  disabled={loadingAliases}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Alias"
                      size="small"
                      helperText={draftScope.alias && !selectedAliasPlacement ? `Selected alias: ${draftScope.alias}` : undefined}
                    />
                  )}
                />
              </Stack>
              {scopeSelectorError && (
                <Alert severity="warning" data-testid={FUNNEL_REPORTS_TEST_IDS.scopeSelectorStatus}>
                  {scopeSelectorError}
                </Alert>
              )}
              {!loadingOnboardings && onboardings.length === 0 && !scopeSelectorError && (
                <Alert severity="info" data-testid={FUNNEL_REPORTS_TEST_IDS.scopeSelectorStatus}>
                  No V2 onboardings found for this project.
                </Alert>
              )}
              <Stack spacing={1}>
                {/* Connect/sync Meta right where its value shows up — no detour
                    through Integrations; OAuth returns to this page. */}
                {projectId && <MetaAdsInlineConnectCard projectId={projectId} />}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <PaidFilterAutocomplete
                    testId={FUNNEL_REPORTS_TEST_IDS.paidCampaignInput}
                    label="Campaign"
                    value={draftPaidFilters.campaign}
                    options={paidFilterOptions.campaign}
                    onValueChange={setPaidFilterValue('campaign')}
                  />
                  <PaidFilterAutocomplete
                    testId={FUNNEL_REPORTS_TEST_IDS.metaAdAccountInput}
                    label="Meta ad account"
                    value={draftPaidFilters.metaAdAccountId}
                    options={paidFilterOptions.metaAdAccountId}
                    onValueChange={setPaidFilterValue('metaAdAccountId')}
                  />
                  <PaidFilterAutocomplete
                    testId={FUNNEL_REPORTS_TEST_IDS.metaCampaignInput}
                    label="Meta campaign"
                    value={draftPaidFilters.metaCampaignId}
                    options={paidFilterOptions.metaCampaignId}
                    onValueChange={setPaidFilterValue('metaCampaignId')}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <PaidFilterAutocomplete
                    testId={FUNNEL_REPORTS_TEST_IDS.metaAdsetInput}
                    label="Meta ad set"
                    value={draftPaidFilters.metaAdsetId}
                    options={paidFilterOptions.metaAdsetId}
                    onValueChange={setPaidFilterValue('metaAdsetId')}
                  />
                  <PaidFilterAutocomplete
                    testId={FUNNEL_REPORTS_TEST_IDS.metaAdInput}
                    label="Meta ad"
                    value={draftPaidFilters.metaAdId}
                    options={paidFilterOptions.metaAdId}
                    onValueChange={setPaidFilterValue('metaAdId')}
                  />
                  <PaidFilterAutocomplete
                    testId={FUNNEL_REPORTS_TEST_IDS.metaCreativeInput}
                    label="Meta creative"
                    value={draftPaidFilters.metaCreativeId}
                    options={paidFilterOptions.metaCreativeId}
                    onValueChange={setPaidFilterValue('metaCreativeId')}
                  />
                  <Autocomplete
                    data-testid={FUNNEL_REPORTS_TEST_IDS.matchQualityInput}
                    options={MATCH_QUALITY_OPTIONS}
                    value={selectedMatchQuality}
                    onChange={handleMatchQualitySelect}
                    fullWidth
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Match quality"
                        size="small"
                        helperText={draftPaidFilters.matchQuality && !selectedMatchQuality ? `Selected quality: ${draftPaidFilters.matchQuality}` : undefined}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Box>
    </FunnelUnitProvider>
  );
}

function isOptionalABDecisionReportMissing(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.message.includes('HTTP 404') || error.message.includes('Cannot GET');
}
